import { supabase } from '@/lib/supabase';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';
import { 
  generateThumbnailUrls, 
  getAnimatedThumbnailUrl, 
  getStaticThumbnailUrl,
  getOptimizedThumbnailOptions 
} from '@/utils/hiveShareThumbnails';
import type { AnimatedThumbnailOptions, ThumbnailUrls } from '@/types/hiveshare';

// HiveShare Types
export interface HiveShareVideo {
  id: string;
  organization_id: string;
  cloudflare_stream_id: string;
  playback_url: string;
  thumbnail_url?: string;
  download_url?: string;
  title: string;
  description?: string;
  duration_seconds?: number;
  file_size_bytes?: number;
  uploaded_by: string;
  upload_status: 'uploading' | 'processing' | 'ready' | 'error' | 'deleted';
  privacy_level: 'organization' | 'selected_families';
  accessible_family_ids: string[];
  tags: string[];
  view_count: number;
  is_active: boolean;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
}

export interface HiveShareUsageMetric {
  id: string;
  organization_id: string;
  metric_type: string;
  metric_date: string;
  metric_value: number;
  source_data: Record<string, any>;
  created_at: string;
}

export interface HiveShareVideoView {
  id: string;
  video_id: string;
  organization_id: string;
  viewer_type: 'staff' | 'parent';
  viewer_id: string;
  view_duration_seconds: number;
  completed_view: boolean;
  device_type: string;
  viewed_at: string;
}

export interface HiveSharePurchase {
  id: string;
  organization_id: string;
  stripe_payment_intent_id?: string;
  stripe_price_id?: string;
  pack_type: string;
  minutes_purchased: number;
  amount_paid: number;
  purchase_date: string;
  activated_at: string;
  expires_at?: string;
}

export interface HiveShareUsageStatus {
  storage_used_minutes: number;
  storage_quota_minutes: number;
  usage_percentage: number;
}

export interface VideoUploadRequest {
  title: string;
  description?: string;
  tags?: string[];
  privacy_level: 'organization' | 'selected_families' | 'create_new_family';
  accessible_family_ids?: string[];
  // New family creation fields
  new_family_name?: string;
  parent_first_name?: string;
  parent_last_name?: string;
  parent_email?: string;
  parent_phone?: string;
}

export interface VideoUploadResponse {
  success: boolean;
  video_id?: string;
  upload_url?: string;
  error?: string;
}

export interface FamilyAccessibleVideo {
  video_id: string;
  title: string;
  description?: string;
  duration_seconds?: number;
  thumbnail_url?: string;
  playback_url: string;
  cloudflare_stream_id: string;
  download_url?: string;
  upload_date: string;
  uploaded_by_name: string;
  tags: string[];
  view_count: number;
  is_favorite: boolean;
}

export interface SignedUrlResponse {
  success: boolean;
  signedUrl?: string;
  expiresAt?: string;
  error?: string;
}

export const HiveShareService = {
  /**
   * Get current storage usage and quota for organization
   * Uses Cloudflare Stream API for accurate storage usage
   */
  async getUsageStatus(organizationId?: string): Promise<HiveShareUsageStatus> {
    const context: ErrorContext = { 
      operation: 'getUsageStatus',
      additionalInfo: { organizationId }
    };

    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      // Get current user session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      // Call the edge function to get accurate storage usage from Cloudflare
      const { data, error } = await supabase.functions.invoke('hiveshare-storage-usage', {
        body: {
          organizationId
        }
      });

      if (error) {
        throw new Error(`Failed to get storage usage: ${error.message}`);
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to get storage usage');
      }

      return {
        storage_used_minutes: data.storage_used_minutes || 0,
        storage_quota_minutes: data.storage_quota_minutes || 100,
        usage_percentage: data.usage_percentage || 0
      };
    } catch (error) {
      handleError(error, ErrorSeverity.HIGH, context);
      throw error;
    }
  },

  /**
   * Check if organization can upload video within quota
   */
  async canUploadVideo(organizationId: string, videoDurationMinutes: number): Promise<boolean> {
    const context: ErrorContext = { 
      operation: 'canUploadVideo',
      additionalInfo: { organizationId, videoDurationMinutes }
    };

    try {
      const { data, error } = await supabase.rpc('can_upload_hiveshare_video', {
        org_id: organizationId,
        video_duration_minutes: videoDurationMinutes
      });

      if (error) {
        throw new Error(`RPC error: ${error.message}`);
      }

      return data;
    } catch (error) {
      handleError(error, ErrorSeverity.MEDIUM, context);
      return false;
    }
  },

  /**
   * Create video upload using secure Supabase Edge Function
   */
  async createVideoUpload(
    organizationId: string,
    uploadRequest: VideoUploadRequest,
    file: File
  ): Promise<VideoUploadResponse> {
    const context: ErrorContext = { 
      operation: 'createVideoUpload',
      additionalInfo: { organizationId, title: uploadRequest.title }
    };

    try {
      // Get current user session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      // Check quota before upload
      const videoDurationMinutes = Math.ceil(file.size / (1024 * 1024 * 10)); // Rough estimate: 10MB per minute
      const canUpload = await this.canUploadVideo(organizationId, videoDurationMinutes);
      
      if (!canUpload) {
        return {
          success: false,
          error: 'Storage quota exceeded. Please purchase additional storage.'
        };
      }

      // Prepare request body
      const requestBody = {
        organizationId,
        title: uploadRequest.title,
        description: uploadRequest.description,
        tags: uploadRequest.tags,
        privacy_level: uploadRequest.privacy_level,
        accessible_family_ids: uploadRequest.accessible_family_ids,
        file_size_bytes: file.size,
        file_name: file.name,
        content_type: file.type,
        // New family creation fields
        new_family_name: uploadRequest.new_family_name,
        parent_first_name: uploadRequest.parent_first_name,
        parent_last_name: uploadRequest.parent_last_name,
        parent_email: uploadRequest.parent_email,
        parent_phone: uploadRequest.parent_phone
      };

      // Call secure Supabase Edge Function for Cloudflare Stream upload
      const { data, error } = await supabase.functions.invoke('hiveshare-upload-video', {
        body: requestBody
      });

      if (error) {
        throw new Error(`Edge function error: ${error.message}`);
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to create video upload');
      }

      return {
        success: true,
        video_id: data.video_id,
        upload_url: data.upload_url
      };
    } catch (error) {
      handleError(error, ErrorSeverity.HIGH, context);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  },

  /**
   * Get videos for organization (admin view)
   */
  async getOrganizationVideos(organizationId?: string): Promise<HiveShareVideo[]> {
    const context: ErrorContext = { 
      operation: 'getOrganizationVideos',
      additionalInfo: { organizationId }
    };

    try {
      // Get current user for favorite status
      const { data: { user } } = await supabase.auth.getUser();
      
      let query = supabase
        .from('hiveshare_videos')
        .select(`
          *,
          hiveshare_user_favorites(id)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

      // Transform the data to include is_favorite field
      const videos = (data || []).map(video => ({
        ...video,
        is_favorite: video.hiveshare_user_favorites && video.hiveshare_user_favorites.length > 0
      }));

      return videos;
    } catch (error) {
      handleError(error, ErrorSeverity.MEDIUM, context);
      throw error;
    }
  },

  /**
   * Get videos for organization (alias for getOrganizationVideos)
   */
  async getVideos(organizationId?: string): Promise<HiveShareVideo[]> {
    return this.getOrganizationVideos(organizationId);
  },

  /**
   * Get videos accessible by family (parent view)
   */
  async getFamilyAccessibleVideos(familyId: string): Promise<FamilyAccessibleVideo[]> {
    const context: ErrorContext = { 
      operation: 'getFamilyAccessibleVideos',
      additionalInfo: { familyId }
    };

    try {
      const { data, error } = await supabase.rpc('get_family_accessible_videos', {
        family_id: familyId
      });

      if (error) {
        throw new Error(`RPC error: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      handleError(error, ErrorSeverity.MEDIUM, context);
      throw error;
    }
  },

  /**
   * Toggle video favorite status for current user
   */
  async toggleVideoFavorite(videoId: string): Promise<{ isFavorite: boolean }> {
    const context: ErrorContext = { 
      operation: 'toggleVideoFavorite',
      additionalInfo: { videoId }
    };

    try {
      // Get current user session
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Check if video is already favorited by this user
      const { data: existingFavorite, error: checkError } = await supabase
        .from('hiveshare_user_favorites')
        .select('id')
        .eq('video_id', videoId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "not found"
        throw new Error(`Failed to check favorite status: ${checkError.message}`);
      }

      if (existingFavorite) {
        // Remove from favorites
        const { error: deleteError } = await supabase
          .from('hiveshare_user_favorites')
          .delete()
          .eq('video_id', videoId)
          .eq('user_id', user.id);

        if (deleteError) {
          throw new Error(`Failed to remove favorite: ${deleteError.message}`);
        }

        return { isFavorite: false };
      } else {
        // Add to favorites
        const { error: insertError } = await supabase
          .from('hiveshare_user_favorites')
          .insert({ 
            video_id: videoId,
            user_id: user.id 
          });

        if (insertError) {
          throw new Error(`Failed to add favorite: ${insertError.message}`);
        }

        return { isFavorite: true };
      }
    } catch (error) {
      handleError(error, ErrorSeverity.MEDIUM, context);
      throw error;
    }
  },

  /**
   * Get user's favorite videos
   */
  async getUserFavoriteVideos(organizationId: string): Promise<HiveShareVideo[]> {
    const context: ErrorContext = { 
      operation: 'getUserFavoriteVideos',
      additionalInfo: { organizationId }
    };

    try {
      const { data, error } = await supabase
        .from('hiveshare_videos')
        .select(`
          *,
          hiveshare_user_favorites!inner(created_at)
        `)
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

      return data.map(video => ({
        ...video,
        is_favorite: true // All videos in this query are favorites
      }));
    } catch (error) {
      handleError(error, ErrorSeverity.HIGH, context);
      throw error;
    }
  },

  /**
   * Record video view analytics
   */
  async recordVideoView(
    videoId: string,
    viewerType: 'staff' | 'parent',
    viewerId: string,
    watchDurationSeconds: number = 0,
    completedView: boolean = false,
    deviceType: string = 'unknown'
  ): Promise<void> {
    const context: ErrorContext = { 
      operation: 'recordVideoView',
      additionalInfo: { videoId, viewerType }
    };

    try {
      const { error } = await supabase.rpc('record_hiveshare_view', {
        video_id: videoId,
        viewer_type: viewerType,
        viewer_id: viewerId,
        watch_duration_seconds: watchDurationSeconds,
        completed_view: completedView,
        device_type: deviceType
      });

      if (error) {
        throw new Error(`RPC error: ${error.message}`);
      }
    } catch (error) {
      handleError(error, ErrorSeverity.LOW, context);
      // Don't throw for analytics errors - just log them
    }
  },

  /**
   * Get video analytics for organization
   */
  async getVideoAnalytics(organizationId: string, dateRange?: { start: string; end: string }) {
    const context: ErrorContext = { 
      operation: 'getVideoAnalytics',
      additionalInfo: { organizationId }
    };

    try {
      let query = supabase
        .from('hiveshare_usage_metrics')
        .select('*')
        .eq('organization_id', organizationId);

      if (dateRange) {
        query = query
          .gte('metric_date', dateRange.start)
          .lte('metric_date', dateRange.end);
      }

      const { data, error } = await query.order('metric_date', { ascending: false });

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      handleError(error, ErrorSeverity.MEDIUM, context);
      throw error;
    }
  },

  /**
   * Update video metadata
   */
  async updateVideo(
    videoId: string,
    updates: Partial<Pick<HiveShareVideo, 'title' | 'description' | 'tags' | 'privacy_level' | 'accessible_family_ids'>>
  ): Promise<void> {
    const context: ErrorContext = { 
      operation: 'updateVideo',
      additionalInfo: { videoId }
    };

    try {
      const { error } = await supabase
        .from('hiveshare_videos')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', videoId);

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }
    } catch (error) {
      handleError(error, ErrorSeverity.MEDIUM, context);
      throw error;
    }
  },

  /**
   * Delete video (soft delete)
   */
  async deleteVideo(videoId: string): Promise<void> {
    const context: ErrorContext = { 
      operation: 'deleteVideo',
      additionalInfo: { videoId }
    };

    try {
      // Call Edge Function to delete from Cloudflare Stream and update database
      const { data, error } = await supabase.functions.invoke('hiveshare-delete-video', {
        body: { video_id: videoId }
      });

      if (error) {
        throw new Error(`Edge function error: ${error.message}`);
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete video');
      }
    } catch (error) {
      handleError(error, ErrorSeverity.HIGH, context);
      throw error;
    }
  },

  /**
   * Get storage pack purchase history
   */
  async getPurchaseHistory(organizationId: string): Promise<HiveSharePurchase[]> {
    const context: ErrorContext = { 
      operation: 'getPurchaseHistory',
      additionalInfo: { organizationId }
    };

    try {
      const { data, error } = await supabase
        .from('hiveshare_purchases')
        .select('*')
        .eq('organization_id', organizationId)
        .order('purchase_date', { ascending: false });

      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      handleError(error, ErrorSeverity.MEDIUM, context);
      throw error;
    }
  },

  /**
   * Create storage pack purchase
   */
  async purchaseStoragePack(
    organizationId: string,
    packType: '1k_minutes' | '5k_minutes' | '10k_minutes'
  ): Promise<{ success: boolean; checkout_url?: string; error?: string }> {
    const context: ErrorContext = { 
      operation: 'purchaseStoragePack',
      additionalInfo: { organizationId, packType }
    };

    try {
      // Call Edge Function to create Stripe checkout for storage pack
      const { data, error } = await supabase.functions.invoke('hiveshare-purchase-storage', {
        body: {
          organization_id: organizationId,
          pack_type: packType
        }
      });

      if (error) {
        throw new Error(`Edge function error: ${error.message}`);
      }

      return data;
    } catch (error) {
      handleError(error, ErrorSeverity.HIGH, context);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Purchase failed'
      };
    }
  },

  /**
   * Get signed URL for video access
   */
  async getSignedVideoUrl(
    videoId: string,
    type: 'iframe' | 'hls' | 'thumbnail' | 'download' = 'iframe',
    expiresIn?: number
  ): Promise<SignedUrlResponse> {
    const context: ErrorContext = { 
      operation: 'getSignedVideoUrl',
      additionalInfo: { videoId, type }
    };

    try {
      // Get current user session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      // Call the edge function to generate signed URL
      const { data, error } = await supabase.functions.invoke('hiveshare-signed-url', {
        body: {
          videoId,
          type,
          expiresIn
        }
      });

      if (error) {
        throw new Error(`Failed to generate signed URL: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error generating signed URL:', error);
      handleError(error, context);
      throw error;
    }
  },

  /**
   * Generate thumbnail URLs for a video
   */
  async getThumbnailUrls(videoId: string, breakpoint?: string): Promise<ThumbnailUrls> {
    const context: ErrorContext = {
      operation: 'getThumbnailUrls',
      additionalInfo: { videoId, breakpoint }
    };

    try {
      const options = {}; // Add any required options here
      return generateThumbnailUrls(videoId, options, breakpoint);
    } catch (error) {
      handleError(error, context);
      return { static: '', animated: '' };
    }
  },

  /**
   * Generate animated thumbnail URL for a video
   */
  getAnimatedThumbnailUrl(
    videoId: string,
    options: Partial<AnimatedThumbnailOptions> = {},
    breakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop'
  ): string {
    const context: ErrorContext = { 
      operation: 'getAnimatedThumbnailUrl',
      additionalInfo: { videoId, breakpoint }
    };

    try {
      return getAnimatedThumbnailUrl(videoId, options, breakpoint);
    } catch (error) {
      handleError(error, ErrorSeverity.LOW, context);
      return '';
    }
  },

  /**
   * Generate static thumbnail URL for a video
   */
  getStaticThumbnailUrl(
    videoId: string,
    options: Partial<Pick<AnimatedThumbnailOptions, 'time' | 'height' | 'width' | 'fit'>> = {},
    breakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop'
  ): string {
    const context: ErrorContext = { 
      operation: 'getStaticThumbnailUrl',
      additionalInfo: { videoId, breakpoint }
    };

    try {
      return getStaticThumbnailUrl(videoId, options, breakpoint);
    } catch (error) {
      handleError(error, ErrorSeverity.LOW, context);
      return '';
    }
  },

  /**
   * Generate optimized thumbnail URLs based on video properties
   */
  getOptimizedThumbnailUrls(
    video: Pick<HiveShareVideo, 'cloudflare_stream_id' | 'duration_seconds'>,
    breakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop'
  ): ThumbnailUrls {
    const context: ErrorContext = { 
      operation: 'getOptimizedThumbnailUrls',
      additionalInfo: { videoId: video.cloudflare_stream_id, breakpoint }
    };

    try {
      const optimizedOptions = getOptimizedThumbnailOptions(
        video.duration_seconds,
        undefined, // aspect ratio - can be calculated later if needed
        breakpoint
      );

      return generateThumbnailUrls(video.cloudflare_stream_id, optimizedOptions, breakpoint);
    } catch (error) {
      handleError(error, ErrorSeverity.LOW, context);
      return { static: '', animated: '' };
    }
  },

  /**
   * Enhanced video list with thumbnail URLs
   */
  async getVideosWithThumbnails(
    organizationId?: string,
    breakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop'
  ): Promise<Array<HiveShareVideo & { thumbnailUrls: ThumbnailUrls }>> {
    const context: ErrorContext = { 
      operation: 'getVideosWithThumbnails',
      additionalInfo: { organizationId, breakpoint }
    };

    try {
      const videos = await this.getVideos(organizationId);
      
      return videos.map(video => ({
        ...video,
        thumbnailUrls: this.getOptimizedThumbnailUrls(video, breakpoint)
      }));
    } catch (error) {
      handleError(error, ErrorSeverity.MEDIUM, context);
      return [];
    }
  },

  /**
   * Enhanced family accessible videos with thumbnail URLs
   */
  async getFamilyAccessibleVideosWithThumbnails(
    familyId: string,
    breakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop'
  ): Promise<Array<FamilyAccessibleVideo & { thumbnailUrls: ThumbnailUrls }>> {
    const context: ErrorContext = { 
      operation: 'getFamilyAccessibleVideosWithThumbnails',
      additionalInfo: { familyId, breakpoint }
    };

    try {
      const videos = await this.getFamilyAccessibleVideos(familyId);
      
      return videos.map(video => ({
        ...video,
        thumbnailUrls: this.getOptimizedThumbnailUrls(
          { 
            cloudflare_stream_id: video.cloudflare_stream_id, 
            duration_seconds: video.duration_seconds 
          }, 
          breakpoint
        )
      }));
    } catch (error) {
      handleError(error, ErrorSeverity.MEDIUM, context);
      return [];
    }
  }
};

export default HiveShareService;